// src/pages/VehicleV2/VehicleDailyProgressBulkInsert.jsx
// Backend expected:
//   app.use("/api/dailyProgressBulkInsert", dailyProgressBulkInsert);
//
// APIs used:
//   GET  {BASE_URL}/api/dailyProgressBulkInsert/init
//   GET  {BASE_URL}/api/dailyProgressBulkInsert/download-template?branchId=&date=&type=
//   POST {BASE_URL}/api/dailyProgressBulkInsert/check
//   POST {BASE_URL}/api/dailyProgressBulkInsert/save
//
// Design aligned with Production BOM Listing:
// - getLS + common headers
// - VITE_BASE_URL
// - header / card / filter-panel / button styles
// - show/hide criteria panel
// - loading + pageLoading + checking + saving states

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSave,
  FaUndo,
  FaCheck,
  FaDownload,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/dailyProgressBulkInsert`;

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const clean = (v) => String(v ?? "").trim();

const VehicleDailyProgressBulkInsert = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Safe localStorage helper (same as listing)
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const getSS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = sessionStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS =
    getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserIdLS =
    getSS("loginUserId") || userIdLS;

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branch = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId = useMemo(
    () => toInt(loginUserIdLS, 0),
    [loginUserIdLS]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const fileRef = useRef(null);

  const [showFilters, setShowFilters] = useState(true);
  const [ms, setMs] = useState("0");
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultFormData = {
    type: "Own",
    date: "",
    file: null,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const isBusy = loading || checking || saving || pageLoading;

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        headers: commonHeaders,
      });

      if (data?.success) {
        setMs(toText(data?.ms ?? "0"));
      } else {
        setMs("0");
      }
    } catch (err) {
      console.error(err);
      setMs("0");
    } finally {
      setPageLoading(false);
    }
  }, [commonHeaders]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const handleChange = useCallback((e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  }, []);

  const handleReset = useCallback(() => {
    setFormData({ ...defaultFormData });

    if (fileRef.current) {
      fileRef.current.value = "";
    }

    toast.info("Form reset");
  }, []);

  const validateBeforeAction = useCallback(
    ({
      requireBranch = false,
      requireLogin = false,
      requireDate = false,
      requireFile = false,
    }) => {
      if (requireBranch && !branchId) {
        toast.error("BranchId not found in localStorage");
        return false;
      }

      if (requireLogin && !loginUserId) {
        toast.error("LoginUserId not found");
        return false;
      }

      if (requireDate && !clean(formData.date)) {
        toast.error("Please select Date");
        return false;
      }

      if (requireFile && !formData.file) {
        toast.error("Please Choose File");
        return false;
      }

      return true;
    },
    [branchId, loginUserId, formData.date, formData.file]
  );

  const handleDownload = useCallback(async () => {
    const ok = validateBeforeAction({
      requireBranch: true,
      requireDate: true,
    });
    if (!ok) return;

    try {
      setLoading(true);

      const { data, headers } = await axios.get(
        `${API_BASE}/download-template`,
        {
          params: {
            branchId,
            date: clean(formData.date),
            type: clean(formData.type),
          },
          headers: commonHeaders,
          responseType: "blob",
        }
      );

      const blob = new Blob([data], {
        type: headers["content-type"] || "application/octet-stream",
      });

      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "DPRBulkInsert.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);

      toast.success("Downloading format template...");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Download failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    validateBeforeAction,
    branchId,
    formData.date,
    formData.type,
    commonHeaders,
  ]);

  const handleCheck = useCallback(async () => {
    const ok = validateBeforeAction({
      requireDate: true,
      requireFile: true,
    });
    if (!ok) return;

    try {
      setChecking(true);

      const fd = new FormData();
      fd.append("date", clean(formData.date));
      fd.append("type", clean(formData.type));
      fd.append("file", formData.file);

      const { data } = await axios.post(`${API_BASE}/check`, fd, {
        headers: {
          ...commonHeaders,
          "Content-Type": "multipart/form-data",
        },
      });

      if (data?.success) {
        toast.success(data?.message || "Checked successfully");
        return;
      }

      const errs = Array.isArray(data?.errors) ? data.errors : [];
      if (errs.length > 0) {
        toast.error(errs.slice(0, 2).join(" | "));
      } else {
        toast.error(data?.message || "Validation failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Check failed");
    } finally {
      setChecking(false);
    }
  }, [validateBeforeAction, formData, commonHeaders]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const ok = validateBeforeAction({
        requireBranch: true,
        requireLogin: true,
        requireDate: true,
        requireFile: true,
      });
      if (!ok) return;

      try {
        setSaving(true);

        const fd = new FormData();
        fd.append("branchId", branchId);
        fd.append("loginUserId", loginUserId);
        fd.append("date", clean(formData.date));
        fd.append("type", clean(formData.type));
        fd.append("file", formData.file);

        const { data } = await axios.post(`${API_BASE}/save`, fd, {
          headers: {
            ...commonHeaders,
            "Content-Type": "multipart/form-data",
          },
        });

        if (!data?.success) {
          throw new Error(data?.message || "Save failed");
        }

        toast.success(data?.message || "Saved Successfully");
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Save failed"
        );
      } finally {
        setSaving(false);
      }
    },
    [validateBeforeAction, branchId, loginUserId, formData, commonHeaders]
  );

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE DAILY PROGRESS BULK INSERT
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Daily Progress Bulk Insert
              </h2>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                Upload Excel
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : checking
                    ? "Checking..."
                    : saving
                    ? "Saving..."
                    : loading
                    ? "Downloading..."
                    : "Processing..."}
                </span>
              )}

              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Details" : "Show Details"}
              </button>
            </div>
          </div>

          {/* ==================== MICROSETTING ==================== */}
          <div
            className={`rounded-lg p-4 mb-6 border text-sm ${
              isDark
                ? "bg-gray-800/50 border-gray-700 text-gray-300"
                : "bg-blue-50 border-blue-100 text-gray-700"
            }`}
          >
            Microsetting (VehicleHSDCalculation):{" "}
            <span className="font-semibold">{ms}</span>
          </div>

          <form onSubmit={handleSubmit}>
            {/* ==================== DETAILS PANEL ==================== */}
            {showFilters && (
              <div
                className={`p-6 rounded-lg mb-6 ${
                  isDark ? "bg-gray-800/50" : "bg-blue-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3
                    className={`text-lg font-semibold ${
                      isDark ? "text-white" : "text-blue-900"
                    }`}
                  >
                    Bulk Insert Details
                  </h3>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isBusy}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                        isDark
                          ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-white"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <FaUndo />
                      Clear
                    </button>

                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={isBusy}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaDownload />
                      {loading ? "Please wait..." : "Download Format"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Type */}
                  <div>
                    <label className={labelClass}>
                      Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-6 h-[42px]">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="type"
                          value="Own"
                          checked={formData.type === "Own"}
                          onChange={handleChange}
                          disabled={isBusy}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span
                          className={
                            isDark ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          Own
                        </span>
                      </label>

                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="type"
                          value="Hire"
                          checked={formData.type === "Hire"}
                          onChange={handleChange}
                          disabled={isBusy}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <span
                          className={
                            isDark ? "text-gray-300" : "text-gray-700"
                          }
                        >
                          Hire
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className={labelClass}>
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={isBusy}
                      required
                    />
                  </div>

                  {/* Upload */}
                  <div className="md:col-span-2">
                    <label className={labelClass}>
                      Upload File <span className="text-red-500">*</span>
                    </label>

                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        isDark
                          ? "border-gray-600 bg-gray-900/40"
                          : "border-blue-200 bg-white"
                      }`}
                    >
                      <input
                        ref={fileRef}
                        type="file"
                        name="file"
                        accept=".xls,.xlsx"
                        onChange={handleChange}
                        className="hidden"
                        id="file-upload"
                        disabled={isBusy}
                        required
                      />

                      <label
                        htmlFor="file-upload"
                        className={`cursor-pointer ${
                          isBusy ? "pointer-events-none opacity-60" : ""
                        }`}
                      >
                        <div className="flex flex-col items-center justify-center">
                          <svg
                            className="w-12 h-12 mx-auto mb-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>

                          <p
                            className={`text-sm ${
                              isDark ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            <span className="font-medium text-blue-600 hover:text-blue-500">
                              Click to upload
                            </span>{" "}
                            or drag and drop
                          </p>

                          <p
                            className={`text-xs mt-1 ${
                              isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Excel files only (.xls / .xlsx)
                          </p>
                        </div>
                      </label>
                    </div>

                    {formData.file && (
                      <p
                        className={`text-sm mt-3 ${
                          isDark ? "text-green-300" : "text-green-600"
                        }`}
                      >
                        Selected file: {toStr(formData.file?.name)}
                      </p>
                    )}

                    <p
                      className={`text-sm mt-3 ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Download the Excel template first so the data is in the
                      correct format.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ==================== ACTION BAR ==================== */}
            <div
              className={`flex flex-col md:flex-row justify-between items-center gap-4 p-4 rounded-lg border ${
                isDark
                  ? "bg-gray-800/40 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <button
                type="button"
                onClick={handleReset}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border transition-all duration-200 ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-700"
                    : "border-gray-300 text-gray-800 hover:bg-gray-100"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaUndo />
                Reset
              </button>

              <div className="flex gap-3 flex-wrap justify-center">
                <button
                  type="button"
                  onClick={handleCheck}
                  disabled={isBusy}
                  className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaCheck />
                  {checking ? "Checking..." : "Check"}
                </button>

                <button
                  type="submit"
                  disabled={isBusy}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isBusy ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSave />
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressBulkInsert;